import React, { useCallback, useRef, useState } from 'react';
import type { ClipboardEvent, FocusEvent, KeyboardEvent } from 'react';
import { FaTimes } from 'react-icons/fa';
import { U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN } from './U2000TagAutocomplete';
import type { U2000CriteriaTag } from './u2000CriteriaTags';
import { u2000InputTagFilterAlphanum } from '../../../../tools/u2000InputTagFilters';

const TAG_BASE =
  'inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-full transition-all duration-150 max-w-xs bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100';
const CONTAINER_BASE =
  'flex w-full min-h-[46px] flex-wrap items-center gap-1.5 px-4 py-2 text-sm bg-surface border rounded-lg transition-all duration-200 border-gray-300 hover:border-gray-400 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-500/20';

const MAX_TAGS_DEFAULT = 50;

function cleanSegment(raw: string, filterFunction: (val: string) => string): string {
  const trimmed = raw.trim().replace(/^[,;\s]+|[,;\s]+$/g, '');
  return filterFunction(trimmed);
}

export interface U2000CriteriaFreeTextTagsInputProps {
  value: U2000CriteriaTag[];
  onChange: (tags: U2000CriteriaTag[]) => void;
  disabled?: boolean;
  placeholder?: string;
  filterFunction?: (val: string) => string;
  /** Une seule valeur (remplace le tag courant). */
  singleValue?: boolean;
  maxTags?: number;
  onAfterSelect?: () => void;
  onDuplicateError?: () => void;
  onMaxTagsError?: (max: number) => void;
}

/**
 * Saisie tags texte libre pour critères recherche string sans lookup (BL-0059).
 */
const U2000CriteriaFreeTextTagsInput: React.FC<U2000CriteriaFreeTextTagsInputProps> = ({
  value,
  onChange,
  disabled = false,
  placeholder,
  filterFunction = u2000InputTagFilterAlphanum,
  singleValue = false,
  maxTags: maxTagsProp,
  onAfterSelect,
  onDuplicateError,
  onMaxTagsError,
}) => {
  const maxTags = singleValue ? 1 : (maxTagsProp ?? MAX_TAGS_DEFAULT);
  const [inputValue, setInputValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const existingValues = useCallback(() => new Set(value.map((t) => t.value)), [value]);

  const tryAddTag = useCallback(
    (raw: string): boolean => {
      const cleaned = cleanSegment(raw, filterFunction);
      if (!cleaned) return false;

      if (singleValue) {
        onChange([{ value: cleaned, label: cleaned }]);
        setInputValue('');
        window.setTimeout(() => onAfterSelect?.(), 0);
        return true;
      }

      if (value.length >= maxTags) {
        onMaxTagsError?.(maxTags);
        return false;
      }
      if (existingValues().has(cleaned)) {
        onDuplicateError?.();
        return false;
      }
      onChange([...value, { value: cleaned, label: cleaned }]);
      setInputValue('');
      return true;
    },
    [filterFunction, singleValue, value, maxTags, existingValues, onChange, onMaxTagsError, onDuplicateError, onAfterSelect],
  );

  const removeTag = useCallback(
    (index: number) => {
      onChange(value.filter((_, i) => i !== index));
      inputRef.current?.focus();
    },
    [value, onChange],
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLInputElement>) => {
      if (singleValue) return;
      const text = e.clipboardData.getData('text');
      if (!text || !U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN.test(text)) return;
      e.preventDefault();
      const segments = text
        .split(U2000_TAG_AUTOCOMPLETE_SPLIT_PATTERN)
        .map((s) => cleanSegment(s, filterFunction))
        .filter(Boolean);
      let next = [...value];
      const seen = new Set(next.map((t) => t.value));
      for (const segment of segments) {
        if (next.length >= maxTags) {
          onMaxTagsError?.(maxTags);
          break;
        }
        if (seen.has(segment)) {
          onDuplicateError?.();
          continue;
        }
        seen.add(segment);
        next = [...next, { value: segment, label: segment }];
      }
      onChange(next);
      setInputValue('');
    },
    [singleValue, filterFunction, value, maxTags, onChange, onMaxTagsError, onDuplicateError],
  );

  const handleInputChange = useCallback(
    (next: string) => {
      setInputValue(filterFunction(next));
    },
    [filterFunction],
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if ((e.key === 'Enter' || e.key === ',') && inputValue.trim() !== '') {
        e.preventDefault();
        tryAddTag(inputValue);
        return;
      }
      if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
        e.preventDefault();
        removeTag(value.length - 1);
      }
    },
    [inputValue, tryAddTag, value.length, removeTag],
  );

  const handleBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (containerRef.current?.contains(e.relatedTarget as Node)) return;
      setIsFocused(false);
      if (inputValue.trim()) {
        tryAddTag(inputValue);
      }
    },
    [inputValue, tryAddTag],
  );

  const inputDisabled = disabled || (!singleValue && value.length >= maxTags);

  return (
    <div
      ref={containerRef}
      className={`${CONTAINER_BASE} ${disabled ? 'cursor-not-allowed bg-muted opacity-70' : ''} ${isFocused ? 'border-blue-500 ring-2 ring-blue-500/20' : ''}`}
      onClick={() => {
        if (!disabled) inputRef.current?.focus();
      }}
    >
      {value.map((tag, idx) => (
        <span key={`${tag.value}-${idx}`} className={TAG_BASE} title={tag.label ?? tag.value}>
          <span className="truncate">{tag.label ?? tag.value}</span>
          {!disabled && (
            <button
              type="button"
              className="rounded-full p-0.5 hover:bg-blue-200/80"
              onClick={(e) => {
                e.stopPropagation();
                removeTag(idx);
              }}
              aria-label={`Retirer ${tag.label ?? tag.value}`}
            >
              <FaTimes className="h-3 w-3" />
            </button>
          )}
        </span>
      ))}
      <input
        ref={inputRef}
        type="text"
        className="min-w-32 flex-1 border-0 bg-transparent p-0 text-sm outline-hidden focus:ring-0 disabled:cursor-not-allowed"
        value={inputValue}
        disabled={inputDisabled}
        placeholder={value.length === 0 ? placeholder : ''}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        aria-label={placeholder ?? 'Valeur critère'}
      />
    </div>
  );
};

export default U2000CriteriaFreeTextTagsInput;
